import {
  LeftOutlined,
  PlusOutlined,
  SearchOutlined,
  UserAddOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Card, Input, Modal, Table } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import useAxios from "../../hooks/useAxios";
import { checkBulkPermissions } from "../../utils/checkPermission";
import useRole from "../../hooks/useRole";

const UnassignedLeads = ({
  setIsUnAssignedOpen,
  setLoading,
  candidates,
  getAllUnassignedLeads,
}) => {
  const history = useHistory();
  const api = useAxios();
  const [openRecruiterDialog, setOpenRecruiterDialog] = useState(false);
  const [allRules, setAllRules] = useState([]);
  const [search, setSearch] = useState("");
  const searchParams = new URLSearchParams(history.location.search);
  const pageFromUrl = parseInt(searchParams.get("page"), 10) || 1;
  const [page, setPage] = useState(pageFromUrl);
  const [pageSize, setPageSize] = useState(10);

  const role = useRole();
  const [permissions, setPermissions] = useState({
    canAddExistingRule: false,
    canCreateNewRule: false,
  });

  useEffect(() => {
    const fetchPermissions = async () => {
      if (!role) return;
      try {
        const checks = [
          { entityType: "manager_dashboard_module", permission: "add_existing_rule" },
          { entityType: "manager_dashboard_module", permission: "create_new_rule" }
        ];
        const results = await checkBulkPermissions(role, checks);
        
        const perms = {
          canAddExistingRule: false,
          canCreateNewRule: false
        };
        results.forEach(res => {
          if (res.permission === "add_existing_rule") perms.canAddExistingRule = res.allowed;
          if (res.permission === "create_new_rule") perms.canCreateNewRule = res.allowed;
        });
        
        setPermissions(perms);
      } catch (err) {
        console.error(err);
      }
    };
    
    fetchPermissions();
  }, [role]);

  const columns = [
    {
      title: "DATE",
      dataIndex: "date",
      key: "date",
    },
    {
      title: "NAME",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <span
          onClick={() =>
            history.push({
              pathname: `/candidate/${record.key}`,
              state: {
                from: `/manager/dashboard?unassigned=true&page=${page}`,
                fromName: "Dashboard",
                unassigned: true,
              },
            })
          }
          className="cursor-pointer hover:underline font-medium"
        >
          {text}
        </span>
      ),
    },
    {
      title: "PHONE NUMBER",
      dataIndex: "phone",
      key: "phone",
      render: (text, record) => (
        <span
          onClick={() =>
            history.push({
              pathname: `/candidate/${record.key}`,
              state: {
                from: `/manager/dashboard?unassigned=true&page=${page}`,
                fromName: "Dashboard",
                unassigned: true,
              },
            })
          }
          className="cursor-pointer hover:underline"
        >
          {text}
        </span>
      ),
    },
    {
      title: "LOCATION",
      dataIndex: "location",
      key: "location",
    },
    {
      title: "QUALIFICATION",
      dataIndex: "qualification",
      key: "qualification",
    },
    {
      title: "EXPERIENCE",
      dataIndex: "experience",
      key: "experience",
    },
  ];

  const getAllRules = async () => {
    try {
      const { data } = await api.get("/ruleEngine", { tokenType: "user" });

      const rawRules =
        data?.map((ele) => {
          const recruiter_name = ele?.recruiter
            ?.map((r) => r?.name)
            ?.join(" | ");
          return {
            _id: ele?._id,
            name: ele?.ruleName || "",
            recruiter: recruiter_name || "",
          };
        }) || [];

      setAllRules(rawRules);
    } catch (err) {
      console.error(err);
    }
  };

  const getPaginatedLeads = async () => {
    try {
      setLoading(true);
      await getAllUnassignedLeads(page, pageSize);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        await Promise.all([getAllRules()]);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  const initialLoad = useRef(true);

  useEffect(() => {
    if(initialLoad.current) {
      initialLoad.current = false;
      return;
    }
    getPaginatedLeads();
  }, [page, pageSize]);

  const filteredRows = candidates?.records?.filter((row) => {
    return (
      row?.name?.toLowerCase().includes(search) ||
      row?.phone?.includes(search) ||
      row?.location?.toLowerCase().includes(search) ||
      row?.qualification?.toLowerCase().includes(search) ||
      row?.experience?.toString().includes(search)
    );
  });

  return (
    <>
      <Card>
        <div className="py-1 px-1 mb-2 flex justify-between items-center">
          <div className="flex items-center gap-[10px]">
            <button
              onClick={(e) => setIsUnAssignedOpen(false)}
              className="flex items-center mt-[2px] outline-none"
            >
              <LeftOutlined className="!text-[18px]" />
            </button>{" "}
            <h4 className="text-[1.5rem] mb-0">New Unassigned Leads</h4>
          </div>
          <div className="flex gap-1">
            {permissions.canAddExistingRule && (
              <button
                onClick={(e) => setOpenRecruiterDialog(true)}
                className="btn-sm btn-primary flax items-center"
              >
                <UserOutlined className="!text-[14px] !font-semibold" />
                &nbsp;&nbsp;
                <span className="font-semibold">Assign to Existing Rule</span>
              </button>
            )}
            {permissions.canCreateNewRule && (
              <button
                onClick={() => history.push(`/rule-engine/create`)}
                className="btn-sm btn-primary flax items-center py-[5px]"
              >
                <PlusOutlined className="!text-[14px] !font-semibold" />
                &nbsp;&nbsp;
                <span className="font-semibold">Create New Rule</span>
              </button>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-[1.5rem] mt-4">
          <div className="flex justify-between">
            <div className="flex-grow">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{
                  borderRadius: "8px",
                  width: "100%",
                  maxWidth: "300px",
                }}
                size="large"
                placeholder="Search leads..."
                prefix={<SearchOutlined />}
              />
            </div>
          </div>
          <div>
            <Table
              columns={columns}
              dataSource={filteredRows}
              pagination={{
                total: candidates?.totalRecords || 0,
                pageSizeOptions: ["5", "10", "30"],
                showSizeChanger: true,
                current: page,
                pageSize: pageSize,
                onChange: (newPage, newPageSize) => {
                  setPage(newPage);
                  history.replace(`/manager/dashboard?unassigned=true&page=${newPage}`);
                  if (newPageSize !== pageSize) {
                    setPageSize(newPageSize);
                    setPage(1);
                    history.replace(`/manager/dashboard?unassigned=true&page=1`);
                  }
                },
              }}
              showSorterTooltip={false}
              scroll={{ x: true }}
              style={{ whiteSpace: "pre" }}
              exportFileName="details"
              size="small"
            />
          </div>
        </div>
      </Card>
      <Modal
        title=""
        className="p-0 custom_modal__"
        centered
        open={openRecruiterDialog}
        width={500}
        onCancel={(e) => setOpenRecruiterDialog(false)}
        footer={null}
      >
        <div>
          <div className="modal-header d-flex justify-content-between align-items-center active-color-modal text-white w-100">
            <h5
              className="modal-title text-white text-capitalize font-weight-bold"
              style={{ fontSize: "18px", fontWeight: 400 }}
            >
              Assign to Existing Rule
            </h5>
          </div>
          <div className="p-4 flex flex-col gap-1">
            {allRules?.map((ele) => {
              return (
                <button
                  onClick={() => history.push(`/rule-engine/edit/${ele?._id}`)}
                  className="outline-none"
                >
                  <div className="card-header flex justify-content-between gap-2 p-2 card-custom">
                    <div className="w-full flex flex-col">
                      <h4 className="text-[1rem] mb-0 w-full text-left">
                        {ele?.name}
                      </h4>
                      <p className="text-[0.8rem] mb-0 w-full text-left">
                        Recruiter: {ele?.recruiter}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </Modal>
    </>
  );
};

export default UnassignedLeads;
